<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import Icon from 'next/icon/Icon.vue';
import OnboardingAPI from 'dashboard/api/onboarding';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  hint: { type: String, default: '' },
});

const emit = defineEmits([
  'update:modelValue',
  'validity-change',
  'provider-change',
]);

const { t } = useI18n();

const status = ref('idle'); // idle | checking | valid | invalid
const statusError = ref('');
const detectedProvider = ref(null); // gmail | outlook | forward | null
const lastCheckedEmail = ref('');

const isValid = computed(() => status.value === 'valid');

const providerIcon = computed(() => {
  if (detectedProvider.value === 'gmail') return 'i-woot-gmail';
  if (detectedProvider.value === 'outlook') return 'i-woot-outlook';
  return null;
});

watch(isValid, value => emit('validity-change', value), { immediate: true });

function looksLikeEmail(value) {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

async function checkEmail(raw) {
  const value = (raw || '').trim().toLowerCase();
  if (!value) {
    status.value = 'idle';
    statusError.value = '';
    detectedProvider.value = null;
    lastCheckedEmail.value = '';
    emit('provider-change', null);
    return { valid: false, provider: null };
  }

  if (!looksLikeEmail(value)) {
    status.value = 'invalid';
    statusError.value = t('ONBOARDING_WIZARD.CHANNEL.EMAIL_INVALID');
    detectedProvider.value = null;
    lastCheckedEmail.value = value;
    emit('provider-change', null);
    return { valid: false, provider: null };
  }

  status.value = 'checking';
  statusError.value = '';
  lastCheckedEmail.value = value;

  try {
    const { data } = await OnboardingAPI.validateEmail(value);
    if (lastCheckedEmail.value !== value) {
      return { valid: false, provider: null };
    }

    if (data?.valid === false) {
      status.value = 'invalid';
      statusError.value =
        data?.error || t('ONBOARDING_WIZARD.CHANNEL.EMAIL_TAKEN');
      detectedProvider.value = null;
      emit('provider-change', null);
      return { valid: false, provider: null };
    }

    const provider = data?.provider || 'forward';
    status.value = 'valid';
    statusError.value = '';
    detectedProvider.value = provider;
    emit('provider-change', provider);
    return { valid: true, provider };
  } catch (error) {
    if (lastCheckedEmail.value !== value) {
      return { valid: false, provider: null };
    }
    status.value = 'invalid';
    statusError.value =
      error?.response?.data?.error ||
      t('ONBOARDING_WIZARD.CHANNEL.EMAIL_CHECK_FAILED');
    detectedProvider.value = null;
    emit('provider-change', null);
    return { valid: false, provider: null };
  }
}

const runCheck = useDebounceFn(raw => checkEmail(raw), 450);

watch(
  () => props.modelValue,
  value => {
    if (!value?.trim()) {
      status.value = 'idle';
      statusError.value = '';
      detectedProvider.value = null;
      emit('provider-change', null);
      return;
    }
    runCheck(value);
  },
  { immediate: true }
);

function onInput(event) {
  emit('update:modelValue', event.target.value);
}

async function recheckNow() {
  return checkEmail(props.modelValue);
}

defineExpose({ recheckNow, status, detectedProvider });

const tooltip = computed(() => {
  if (status.value === 'checking') {
    return t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_DETECTING');
  }
  if (status.value === 'valid') {
    if (detectedProvider.value === 'gmail') {
      return t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_AUTODETECT', {
        provider: t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_GMAIL'),
      });
    }
    if (detectedProvider.value === 'outlook') {
      return t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_AUTODETECT', {
        provider: t('ONBOARDING_WIZARD.CHANNEL.PROVIDER_OUTLOOK'),
      });
    }
    return t('ONBOARDING_WIZARD.CHANNEL.EMAIL_OK');
  }
  if (status.value === 'invalid') {
    return statusError.value || t('ONBOARDING_WIZARD.CHANNEL.EMAIL_INVALID');
  }
  return '';
});
</script>

<template>
  <div class="w-full flex flex-col gap-2">
    <label
      v-if="label"
      class="text-sm font-semibold text-slate-700 dark:text-slate-200"
    >
      {{ label }}
    </label>

    <div
      class="flex items-center h-12 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden focus-within:ring-2 focus-within:ring-woot-500 focus-within:border-woot-500 transition-colors"
      :class="{
        'border-green-500 dark:border-green-500': status === 'valid',
        'border-red-400 dark:border-red-500': status === 'invalid',
      }"
    >
      <input
        type="email"
        :value="modelValue"
        :placeholder="placeholder"
        class="reset-base flex-1 h-full px-4 text-base bg-transparent text-slate-700 dark:text-slate-200 outline-none min-w-0 placeholder:text-slate-400 !mb-0"
        @input="onInput"
      />

      <div
        v-if="status !== 'idle'"
        v-tooltip.top="tooltip"
        class="flex-shrink-0 flex items-center justify-center w-11 h-full border-l border-slate-200 dark:border-slate-700"
      >
        <span
          v-if="status === 'checking'"
          class="size-4 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
        />
        <Icon
          v-else-if="status === 'valid' && providerIcon"
          :icon="providerIcon"
          class="size-5"
        />
        <span
          v-else-if="status === 'valid'"
          class="i-lucide-check size-5 text-green-500"
        />
        <span v-else class="i-lucide-x size-5 text-red-500" />
      </div>
    </div>

    <p
      v-if="status === 'invalid' && statusError"
      class="text-sm text-red-600 dark:text-red-400 m-0"
    >
      {{ statusError }}
    </p>
    <p v-else-if="hint" class="text-sm text-slate-500 dark:text-slate-400 m-0">
      {{ hint }}
    </p>
  </div>
</template>
